import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { liaAPI, exportAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Scale, ExternalLink, Trash2, MoreHorizontal, FileDown, FileText } from 'lucide-react';
import { toast } from 'sonner';

export const LIATable = ({ assessments, onRefresh }) => {
  const navigate = useNavigate();
  const { isComplianceReadOnly } = useAuth();

  if (!assessments.length) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Scale className="h-4 w-4 text-amber-500" />
          Legitimate Interest Assessments
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead className="flex-1">Title</TableHead>
              <TableHead>Decision</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assessments.map((lia) => (
              <TableRow
                key={lia.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/risk/lia/${lia.id}`)}
              >
                <TableCell className="font-mono text-sm">{lia.lia_reference || '-'}</TableCell>
                <TableCell>
                  {/* Spec §LIA table — "Unknown" in amber text if entity not assigned */}
                  {lia.entity_name
                    ? lia.entity_name
                    : <span className="text-amber-600 dark:text-amber-400 font-medium">Unknown</span>}
                </TableCell>
                <TableCell className="font-medium flex-1">{lia.title || 'Untitled LIA'}</TableCell>
                <TableCell>
                  {lia.dpo_can_rely === 'yes' && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Can Rely</Badge>}
                  {lia.dpo_can_rely === 'no' && <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20">Cannot Rely</Badge>}
                  {lia.dpo_can_rely === 'conditional' && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">Conditional</Badge>}
                  {!lia.dpo_can_rely && <span className="text-slate-500 text-sm">—</span>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">{lia.status?.replace('_', ' ')}</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {lia.created_at ? new Date(lia.created_at).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {/* Spec §LIA table — Actions: Open, Export DOCX, Export PDF, Delete */}
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/risk/lia/${lia.id}`); }}>
                        <ExternalLink className="h-4 w-4 mr-2" />Open
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const res = await exportAPI.liaDocx(lia.id);
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a'); a.href = url;
                          a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'LIA_Report.docx';
                          document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                          toast.success('DOCX exported');
                        } catch { toast.error('Export failed'); }
                      }} data-testid={`export-lia-docx-${lia.id}`}>
                        <FileDown className="h-4 w-4 mr-2" />Export DOCX
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        // Spec requires Export PDF. No backend PDF generator exists today.
                        toast.info('PDF export is coming soon — please use Export DOCX for now.');
                      }} data-testid={`export-lia-pdf-${lia.id}`}>
                        <FileText className="h-4 w-4 mr-2" />Export PDF
                      </DropdownMenuItem>
                      {!isComplianceReadOnly && (
                        <DropdownMenuItem
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this LIA?')) {
                              try {
                                await liaAPI.delete(lia.id);
                                toast.success('LIA deleted');
                                onRefresh();
                              } catch { toast.error('Failed to delete'); }
                            }
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
